import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const LINES = ["Coffee catering in Cyprus —", "slow coffee, poured warm,", "wherever you gather."];

const HERO_IMG =
  "https://images.unsplash.com/photo-1558996338-294a904a1815?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxjb2ZmZWUlMjBjYXRlcmluZyUyMHNldHVwfGVufDB8fHx8MTc4NzgxMjMyOXww&ixlib=rb-4.1.0&q=85&w=1200&auto=format&fit=crop";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            data-testid="hero-overline"
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-matcha mb-3"
          >
            Mobile coffee catering — est. in your neighborhood
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            data-testid="hero-powered-by"
            className="flex items-center gap-2 mb-8"
          >
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-espresso/40">Powered by Coffee Pause</span>
          </motion.div>
          <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.14, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === 2 ? <em className="italic text-matcha">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-8 max-w-md text-base leading-relaxed text-espresso/70"
          >
            Our mobile coffee bar travels all over Cyprus — wedding coffee
            catering, corporate mornings in Nicosia, markets and backyard
            parties — serving café-quality cups with the same care we pour at
            the shop.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#contact" data-testid="hero-cta-book"
              onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
              className="rounded-full bg-espresso text-alabaster px-8 py-3.5 font-mono text-[11px] uppercase tracking-widest hover:bg-matcha transition-colors duration-300">
              Plan your event
            </a>
            <a href="#offerings" data-testid="hero-cta-offerings"
              onClick={(e) => { e.preventDefault(); document.querySelector("#offerings")?.scrollIntoView({ behavior: "smooth" }); }}
              className="rounded-full border border-espresso/30 px-8 py-3.5 font-mono text-[11px] uppercase tracking-widest hover:bg-espresso hover:text-alabaster transition-colors duration-300">
              What we serve
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border border-foreground/10 aspect-[4/5]" data-testid="hero-image-frame">
            <motion.img
              src={HERO_IMG}
              alt="Brew & Co. mobile coffee bar serving fresh espresso at an event in Cyprus"
              fetchPriority="high"
              style={{ y: imgY }}
              className="w-full h-[115%] object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-matcha text-alabaster rounded-full w-28 h-28 flex items-center justify-center text-center font-mono text-[10px] uppercase tracking-widest leading-relaxed rotate-[-8deg]" data-testid="hero-badge">
            Espresso<br />on wheels
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-espresso/50"
      >
        <span className="font-mono text-[10px] uppercase tracking-widest">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
}
